/*----------------------------------
- AGENDA PAGE STATE
----------------------------------*/

/**
 * @docs docs/features/agenda
 * @adr  ADR-0055
 * @adr  ADR-0060
 * @rule One URL per agenda state: the page 301s any query whose agenda keys differ from
 *       `buildAgendaPageHref` (junk values, explicit defaults, key order, an inert filter) or that
 *       carries a retired key (`view`), and every link it renders is built here, so the crawlable
 *       URL space is the normalized one. Every month of the window is a page, a past one listed
 *       from its first day.
 */

/*
 * Shared by the `/agenda` page (SSR and client navigation) and the Website gateway that reads
 * Product, so both normalize a query the same way and the read cache keys on the same state.
 * The codec itself (`parseAgendaFilters`, `buildAgendaHref`) is the shared contract; this file
 * adds the page's own rules on top of it.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import {
    AGENDA_LAUNCHES_CATEGORY,
    AGENDA_PATH,
    AGENDA_QUERY_KEYS,
    AGENDA_RETIRED_QUERY_KEYS,
    buildAgendaHref,
    isExpiredAgendaMonth,
    parseAgendaFilters,
    resolveAgendaMonthWindow,
    type AgendaFilters,
    type AgendaSearchInput,
    type ParsedAgendaState,
    type PublicAgendaRequest,
} from '@uniquedomains/components/contracts/agenda/public';

/*----------------------------------
- CONSTANTS
----------------------------------*/

const AGENDA_QUERY_KEY_SET: ReadonlySet<string> = new Set(AGENDA_QUERY_KEYS);
const AGENDA_RETIRED_QUERY_KEY_SET: ReadonlySet<string> = new Set(AGENDA_RETIRED_QUERY_KEYS);

/*----------------------------------
- STATE
----------------------------------*/

/**
 * Drops a filter that cannot change the answer, so two URLs never name one state (the filter
 * chips would otherwise link both): a country filters events only (D7), so it is inert once
 * only TLD entries remain (`category=launches` or a `tld`); and `category=launches` keeps TLD
 * entries only, which a `tld` filter already does.
 */
const dropInertAgendaFilters = (filters: AgendaFilters): AgendaFilters => {
    const launches = filters.category === AGENDA_LAUNCHES_CATEGORY;
    return {
        ...filters,
        category: launches && filters.tld !== null ? null : filters.category,
        country: launches || filters.tld !== null ? null : filters.country,
    };
};

/** The codec's reading of a query, with an inert filter dropped (`dropInertAgendaFilters`). */
export const resolveAgendaPageState = (search: AgendaSearchInput, now: Date): ParsedAgendaState => {
    const state = parseAgendaFilters(search, now);
    return { ...state, filters: dropInertAgendaFilters(state.filters) };
};

/** The one href of a state: every link the page renders and every 301 target. */
export const buildAgendaPageHref = (filters: AgendaFilters, now: Date): string =>
    buildAgendaHref(AGENDA_PATH, dropInertAgendaFilters(filters), { now });

/*----------------------------------
- REDIRECTS
----------------------------------*/

const readHrefQuery = (href: string): URLSearchParams => new URLSearchParams(href.includes('?') ? href.slice(href.indexOf('?') + 1) : '');

/**
 * The 301 target when the request's agenda keys differ from the normalized href of `filters`,
 * or when it carries a retired key (`view`, whose presence alone redirects and which is dropped),
 * else null. Only the four agenda keys are compared; any other parameter (a campaign tag, a
 * click id) is carried over after them, in its order, and never triggers a redirect on its own.
 */
export const resolveAgendaRedirect = (requestUrl: URL, filters: AgendaFilters, now: Date): string | null => {
    const canonicalQuery = readHrefQuery(buildAgendaPageHref(filters, now));
    const requestedQuery = new URLSearchParams();
    const otherParams: Array<[string, string]> = [];
    let carriesRetiredKey = false;
    requestUrl.searchParams.forEach((value, key) => {
        if (AGENDA_RETIRED_QUERY_KEY_SET.has(key)) carriesRetiredKey = true;
        else if (AGENDA_QUERY_KEY_SET.has(key)) requestedQuery.append(key, value);
        else otherParams.push([key, value]);
    });

    if (!carriesRetiredKey && requestedQuery.toString() === canonicalQuery.toString()) return null;

    otherParams.forEach(([key, value]) => canonicalQuery.append(key, value));
    const query = canonicalQuery.toString();
    return query ? `${AGENDA_PATH}?${query}` : AGENDA_PATH;
};

/**
 * The 301 of a month that has left the window at its past end (`isExpiredAgendaMonth`: a link a
 * sent Wednesday email froze), else null: the same state without `month`, so the current month
 * opens, any other parameter carried over. The page answers NotFound for any other out-of-window
 * month. A TLD the allowlist drops costs a second hop here, a rare case of a rare case.
 */
export const resolveAgendaExpiredMonthRedirect = (requestUrl: URL, state: ParsedAgendaState, now: Date): string | null =>
    isExpiredAgendaMonth(requestUrl.searchParams, resolveAgendaMonthWindow(now)) ? resolveAgendaRedirect(requestUrl, state.filters, now) : null;

/**
 * The 301 the page answers before reading Product, else null. A state naming a TLD waits for
 * Product's echo instead: the allowlist may drop the TLD, and the one redirect decided from the
 * echo then covers the rest of the normalization too, so any agenda URL reaches its canonical
 * form in one hop (`?country=US&tld=zzz` goes straight to `/agenda`).
 */
export const resolveAgendaRedirectBeforeRead = (requestUrl: URL, state: ParsedAgendaState, now: Date): string | null =>
    state.filters.tld === null ? resolveAgendaRedirect(requestUrl, state.filters, now) : null;

/*----------------------------------
- PRODUCT READ
----------------------------------*/

/**
 * `Events.ListPublicAgenda` input for one page state: the whole board in one read. The month is
 * always sent, so Product answers for the month the page resolved rather than for its own
 * clock's current month.
 */
export const toAgendaPageReadRequest = (filters: AgendaFilters): PublicAgendaRequest => ({
    range: 'board',
    month: filters.month,
    ...(filters.category ? { category: filters.category } : {}),
    ...(filters.country ? { country: filters.country } : {}),
    ...(filters.tld ? { tld: filters.tld } : {}),
});

/** The read cache key of a normalized request: one entry per agenda state, never per raw query. */
export const buildAgendaReadKey = (request: PublicAgendaRequest): string =>
    [request.range, request.month ?? '', request.category ?? '', request.country ?? '', request.tld ?? ''].join('|');
